import 'react';
import { formatCurrency } from '@atc/string-fns';
import { ErrorMessages, Text, LoadingSpinner } from 'reaxl';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
const getCustomFees = taxesFees => {
  if (!taxesFees || typeof taxesFees !== 'object') {
    return [];
  }
  const excludedKeys = ['totalTaxesFees', 'totalTax', 'showTaxTotalOnly', 'dealerFees', 'dmvFees', 'lenderFees', 'capCost', 'dueInAdvance'];
  return Object.entries(taxesFees).filter(_ref => {
    let [key] = _ref;
    return !excludedKeys.includes(key);
  }).map(_ref2 => {
    let [key, value] = _ref2;
    // Format the label properly with capitalization
    let label;
    if (key.includes(' ')) {
      // For keys with spaces, capitalize each word
      label = key.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    } else {
      // For camelCase, add spaces and capitalize first letter
      label = key.replace(/([A-Z])/g, ' $1').replace(/^(.)/, s => s.toUpperCase());
    }
    return {
      key,
      label,
      value
    };
  });
};

/**
 * Renders the total taxes and fees summary section with conditional display logic
 * @param {string} label - The label text to display (e.g., "Total Financed", "Total Due at Signing")
 * @param {number} total - The total amount to display
 * @param {string} classname - CSS class names to apply to the container div
 * @param {boolean} [showTotalTaxesOnly=false] - Whether to show only tax totals (hides this section if true)
 * @returns {React.ReactElement|boolean} The rendered total section or false if conditions not met
 */
const renderTotalTaxesFees = function (label, total, classname) {
  let showTotalTaxesOnly = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
  return !showTotalTaxesOnly && !!total && /*#__PURE__*/jsxs("div", {
    className: classname,
    children: [/*#__PURE__*/jsx(Text, {
      weight: "bold",
      children: label
    }), /*#__PURE__*/jsx(Text, {
      weight: "bold",
      children: `${formatCurrency(Math.floor(total))}`
    })]
  });
};

/**
 * Renders a list of individual fee items with formatted currency values
 * @param {Array} fees - Array of fee objects with key, label, and value properties
 * @param {string} classnames - CSS class names to apply to each list item
 * @returns {Array<React.ReactElement>} Array of rendered fee list items
 */
const renderFeesList = (fees, classnames) => fees.map(_ref3 => {
  let {
    key,
    label,
    value
  } = _ref3;
  return !!value && /*#__PURE__*/jsxs("li", {
    className: classnames,
    children: [/*#__PURE__*/jsx("span", {
      children: label
    }), /*#__PURE__*/jsx(Text, {
      className: "text-nowrap",
      children: `+ ${formatCurrency(Math.floor(value))}`
    })]
  }, key);
});

/**
 * Renders a complete fee section with title, individual fees list, and total
 * @param {string} title - The section title (e.g., "Due at Signing", "Financed")
 * @param {Array} feesList - Array of fee objects to render
 * @param {string} totalLabel - Label for the total amount row
 * @param {number} totalAmount - The total amount to display
 * @param {string} [className=''] - Additional CSS classes for the section
 * @param {boolean} [isEstimate=false] - Whether to prefix title with "Estimated"
 * @param {boolean} [isLoading=false] - Whether to show loading spinner instead of fees
 * @param {boolean} showTotalTaxesOnly - Whether to show only tax totals (affects total display)
 * @returns {React.ReactElement} The rendered fee section component
 */
const renderFeeSection = function (title, feesList, totalLabel, totalAmount) {
  let className = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : '';
  let isEstimate = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : false;
  let isLoading = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : false;
  let showTotalTaxesOnly = arguments.length > 7 ? arguments[7] : undefined;
  return /*#__PURE__*/jsxs("ul", {
    className: `list-unstyled margin-top-3 margin-bottom-0 ${className}`,
    children: [!!title && /*#__PURE__*/jsxs(Text, {
      color: "primary",
      weight: "bold",
      size: 400,
      children: [isEstimate ? 'Estimated ' : '', title]
    }), isLoading && /*#__PURE__*/jsx(LoadingSpinner, {
      className: "margin-vertical-3 margin-left-0"
    }), !isLoading && renderFeesList(feesList, 'display-flex justify-content-between gap-3'), !isLoading && renderTotalTaxesFees(totalLabel, totalAmount, 'display-flex justify-content-between gap-3', showTotalTaxesOnly)]
  });
};

/**
 * Main function to render the complete taxes and fees breakdown
 * @param {boolean} isLoading - Whether taxes are currently being loaded
 * @param {boolean} hasError - Whether there was an error loading taxes
 * @param {Object} taxesFees - Object containing all taxes and fees data
 * @param {boolean} showTotalTaxesOnly - Whether to show only tax totals
 * @param {boolean} isEstimate - Whether amounts are estimates
 * @param {boolean} isLease - Whether this is a lease deal
 * @param {boolean} [isOfferBreakdownModal=false] - Whether rendered in offer breakdown modal
 * @returns {React.ReactElement} The complete taxes and fees breakdown component
 */
const renderTaxesFeesBreakdown = function (isLoading, hasError, taxesFees, showTotalTaxesOnly, isEstimate, isLease) {
  let isOfferBreakdownModal = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : false;
  const {
    capCost = {},
    dueInAdvance = {}
  } = taxesFees || {};
  const {
    capitalizedDealerFees,
    capitalizedGovFees,
    capitalizedLenderFees,
    capitalizedTaxes,
    capitalizedTaxesAndFees
  } = capCost;
  const {
    diaDealerFees,
    diaGovFees,
    diaLenderFees,
    diaTaxes
  } = dueInAdvance;
  const totalDiaTaxesFees = (diaTaxes ?? 0) + (diaLenderFees ?? 0) + (diaGovFees ?? 0) + (diaDealerFees ?? 0);
  if (!isLoading && hasError) {
    return /*#__PURE__*/jsx(ErrorMessages, {
      errors: ["We're sorry. We couldn't calculate taxes & fees for your location. Talk to the dealer about final calculations."]
    });
  }

  // Define fee lists for different payment types
  const financeAndCashFees = [{
    key: 'taxes',
    label: 'Taxes',
    value: taxesFees?.totalTax
  }, {
    key: 'lenderFees',
    label: 'Lender Fees',
    value: taxesFees?.lenderFees
  }, {
    key: 'dmvFees',
    label: 'DMV Fees',
    value: taxesFees?.dmvFees
  }, {
    key: 'dealerFees',
    label: isEstimate ? 'Estimated Dealer Fees' : 'Dealer Fees',
    value: taxesFees?.dealerFees
  }];

  // Handle custom keys in taxesFees object (e.g., "Documentation Fee")
  const customFees = getCustomFees(taxesFees);
  if (customFees.length > 0) {
    financeAndCashFees.push(...customFees);
  }
  const leaseDueAtSigningFees = [{
    key: 'diaTaxes',
    label: 'Taxes',
    value: diaTaxes
  }, {
    key: 'diaLenderFees',
    label: 'Lender Fees',
    value: diaLenderFees
  }, {
    key: 'diaGovFees',
    label: 'DMV Fees',
    value: diaGovFees
  }, {
    key: 'diaDealerFees',
    label: 'Dealer Fees',
    value: diaDealerFees
  }];
  const leaseCapitalizedFees = [{
    key: 'capTaxes',
    label: 'Taxes',
    value: capitalizedTaxes
  }, {
    key: 'capLenderFees',
    label: 'Lender Fees',
    value: capitalizedLenderFees
  }, {
    key: 'capGovFees',
    label: 'DMV Fees',
    value: capitalizedGovFees
  }, {
    key: 'capDealerFees',
    label: 'Dealer Fees',
    value: capitalizedDealerFees
  }];
  const totalAmount = showTotalTaxesOnly ? taxesFees?.totalTaxes : taxesFees?.totalTaxesFees;
  return /*#__PURE__*/jsxs(Fragment, {
    children: [!isOfferBreakdownModal && /*#__PURE__*/jsx("hr", {
      className: "margin-vertical-0"
    }), /*#__PURE__*/jsx("div", {
      className: isOfferBreakdownModal ? '' : 'margin-top-4',
      children: isLease ? /*#__PURE__*/jsxs(Fragment, {
        children: [(isLoading || !!totalDiaTaxesFees) && renderFeeSection(isOfferBreakdownModal ? undefined : 'Due at Signing', leaseDueAtSigningFees, 'Total Due at Signing', totalDiaTaxesFees, isOfferBreakdownModal ? '' : 'margin-top-4', isEstimate, isLoading, showTotalTaxesOnly), (isLoading || !!capitalizedTaxesAndFees) && renderFeeSection(isOfferBreakdownModal ? undefined : 'Financed', leaseCapitalizedFees, 'Total Financed', capitalizedTaxesAndFees, isOfferBreakdownModal ? '' : 'margin-top-4', isEstimate, isLoading, showTotalTaxesOnly)]
      }) : renderFeeSection(isOfferBreakdownModal ? undefined : 'Financed', financeAndCashFees, 'Total Financed', totalAmount, isOfferBreakdownModal ? '' : 'margin-top-4', isEstimate, isLoading, showTotalTaxesOnly)
    })]
  });
};
export { renderTaxesFeesBreakdown };
